import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFollowUp, evaluateInterview, saveSession } from "../../services/aiInterviewService";

export default function InterviewSession() {
  const { state } = useLocation();
  const navigate = useNavigate();

  if (!state || !state.questions) {
    return (
      <div style={styles.stage}>
        <div style={styles.card}>
          <p style={styles.title}>No interview set up yet.</p>
          <button style={styles.cta} onClick={() => navigate("/erp/ai-interview/setup")}>
            Set up an interview ▸
          </button>
        </div>
      </div>
    );
  }

  const { type, role, difficulty, duration, questions } = state;
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [transcript, setTranscript] = useState([]);
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(duration * 60);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const currentQuestion = questions[index];

  async function handleNext() {
    if (!answer.trim()) return;
    const line = { question: currentQuestion, answer, timestamp: new Date().toISOString() };
    const newTranscript = [...transcript, line];
    setTranscript(newTranscript);
    setAnswer("");

    if (index + 1 < questions.length) {
      setIndex(index + 1);
    } else {
      await finishInterview(newTranscript);
    }
  }

  async function finishInterview(finalTranscript) {
    setSubmitting(true);
    try {
      const evaluation = await evaluateInterview({ transcript: finalTranscript, type, role });
      const saved = await saveSession({
        type,
        role,
        difficulty,
        persona: "professional",
        duration,
        status: "completed",
        transcript: finalTranscript,
        evaluation,
      });
      navigate("/erp/ai-interview/results", { state: { evaluation, transcript: finalTranscript, role, type } });
    } catch (e) {
      navigate("/erp/ai-interview/results", {
        state: { error: e.message || "Could not score this interview.", transcript: finalTranscript, role, type },
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.stage}>
      <div style={{ ...styles.glow, opacity: recording ? 0.35 : 0.18 }} />

      <div style={styles.metaBar}>
        <span style={styles.scene}>SCENE {String(index + 1).padStart(2, "0")} / {String(questions.length).padStart(2, "0")}</span>
        <span style={styles.timer}>⏱ {mm}:{ss}</span>
      </div>

      <div style={styles.questionWrap}>
        <p style={styles.questionLabel}>{type === "hr" ? "HR ROUND" : "TECHNICAL ROUND"} · {role}</p>
        <h1 style={styles.question}>{currentQuestion}</h1>
      </div>

      <div style={styles.answerCard}>
        <div style={styles.micRow}>
          <span style={{ ...styles.micDot, background: recording ? "#4E9C7C" : "#4B5566" }} />
          <span style={styles.micText}>{recording ? "listening…" : "typing your answer"}</span>
        </div>
        <textarea
          style={styles.textarea}
          value={answer}
          onFocus={() => setRecording(true)}
          onBlur={() => setRecording(false)}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Type your answer here…"
          rows={5}
        />
        <div style={styles.actionRow}>
          <span style={styles.hint}>{transcript.length} of {questions.length} answered</span>
          <button style={styles.cta} onClick={handleNext} disabled={submitting || !answer.trim()}>
            {submitting
              ? "Scoring the scene…"
              : index + 1 < questions.length
              ? "Next question ▸"
              : "Finish interview ▸"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  stage: {
    minHeight: "100vh",
    background: "#10151F",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'IBM Plex Sans', sans-serif",
    padding: "28px 40px",
    boxSizing: "border-box",
  },
  glow: {
    position: "absolute",
    top: "-15%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "800px",
    height: "800px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(201,154,75,0.25) 0%, rgba(201,154,75,0) 70%)",
    pointerEvents: "none",
    transition: "opacity 0.6s ease",
  },
  metaBar: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: "12px",
    color: "#C99A4B",
    letterSpacing: "1px",
    borderTop: "2px solid #C99A4B",
    paddingTop: "10px",
  },
  questionWrap: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    maxWidth: "760px",
    margin: "0 auto",
  },
  questionLabel: {
    color: "#8A93A6",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: "11px",
    letterSpacing: "2px",
    marginBottom: "18px",
  },
  question: {
    fontFamily: "'Fraunces', serif",
    color: "#EFEAE1",
    fontSize: "32px",
    fontWeight: 600,
    lineHeight: 1.35,
    margin: 0,
  },
  answerCard: {
    position: "relative",
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "18px",
    padding: "22px 26px",
    maxWidth: "760px",
    width: "100%",
    margin: "0 auto",
  },
  micRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  micDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    transition: "background 0.3s ease",
  },
  micText: {
    color: "#6B7488",
    fontSize: "11px",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  textarea: {
    width: "100%",
    background: "#1B2231",
    border: "1px solid #2A3345",
    borderRadius: "12px",
    padding: "14px",
    color: "#EFEAE1",
    fontSize: "14px",
    fontFamily: "'IBM Plex Sans', sans-serif",
    resize: "vertical",
    boxSizing: "border-box",
    marginBottom: "16px",
  },
  actionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  hint: {
    color: "#6B7488",
    fontSize: "11px",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  cta: {
    background: "#C99A4B",
    color: "#10151F",
    border: "none",
    borderRadius: "10px",
    padding: "12px 22px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
  },
  card: {
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "18px",
    padding: "30px",
    margin: "auto",
    textAlign: "center",
  },
  title: {
    color: "#EFEAE1",
    marginBottom: "16px",
  },
};
